// LeaveRequestPortal — driver-facing "Request Time Away" tab for the portal.
//
// This is a native React port of the proven request flow that previously lived
// only behind the QR code (Public/roster-calendar.html?mode=request). It reads
// and writes the SAME dataset (/.netlify/functions/roster-data → Roster_Data),
// produces the SAME pending-entry shape the manager approval queue expects, and
// reuses the same pure validation/availability rules — so QR and in-portal
// requests are interchangeable.
//
// Rule (team decision): ONE confirmed (approved) booking blocks the whole day
// for everyone else in that team. The day shows N/A and the driver is told to
// speak to their team leader — we never reveal who is away (confidential).
import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, AlertCircle, CheckCircle, Lock } from 'lucide-react';
import {
  workingDayCount, addMonths, approvedWorkingDays, checkAnnualAllowance,
  HOLIDAY_TYPES, SICK_TYPES, ANNUAL_ALLOWANCE,
} from '../utils/leaveRules';

const API   = '/.netlify/functions/roster-data';
const SMS   = '/.netlify/functions/send-sms';
const DAYS  = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const TEAMS = ['City', 'Darlinghurst', 'Broadway'];
const TEAM_COLOR = { City: 'var(--hp-blue)', Darlinghurst: 'var(--hp-green)', Broadway: 'var(--hp-purple)' };

// ── Pure helpers (ported verbatim from roster-calendar.html) ────────────────
function toISO(y, m, d) {
  const dt = new Date(y, m, d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
function localToday() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}
function fmtShort(ds) { return new Date(ds + 'T12:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); }
function fmtFull(ds)  { return new Date(ds + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); }
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

// entry.team can be comma-separated (multi-area driver) — match if any matches
function teamMatch(entryTeam, myTeam) {
  if (!entryTeam || !myTeam) return false;
  return entryTeam.split(',').map(t => t.trim()).includes(myTeam);
}
// Allowance rules (working days less NSW public holidays, rolling-12-month cap)
// live in ../utils/leaveRules — the same module roster-data.js enforces on write.
// The driver is never told a holiday was skipped; the tally is just smaller.
// Annual-leave blackout: 1 Nov – 24 Dec
function isBlackout(ds) {
  const d = new Date(ds + 'T12:00:00');
  const m = d.getMonth() + 1, day = d.getDate();
  return (m === 11) || (m === 12 && day <= 24);
}
function rangeHasBlackout(s, e) {
  const d = new Date(s + 'T12:00:00');
  const end = new Date(e + 'T12:00:00');
  while (d <= end) { if (isBlackout(d.toISOString().slice(0, 10))) return true; d.setDate(d.getDate() + 1); }
  return false;
}
const BLOCKED_MSG = 'This day is not available. Please discuss with your team leader.';

export default function LeaveRequestPortal({ shift, oversight = false }) {
  const today = localToday();

  // Driver's team(s). Session carries `routes` (array) after login; fall back to
  // the legacy single `route` string (comma/slash separated for multi-area).
  const routeTeams = useMemo(() => {
    const raw = Array.isArray(shift?.routes) && shift.routes.length
      ? shift.routes
      : String(shift?.route || '').split(/[,/]/);
    const cleaned = raw.map(t => t.trim()).filter(Boolean)
      .map(t => TEAMS.find(v => v.toLowerCase() === t.toLowerCase()) || t);
    return [...new Set(cleaned)].filter(t => TEAMS.includes(t));
  }, [shift]);

  // The in-app shift session can lose the driver's team (the server shift backup
  // doesn't store a route, so a mid-shift reload rebuilds the session with route:'').
  // When that happens the session-derived team is empty even though the driver DOES
  // have one — so we re-fetch it from their authenticated profile and self-heal,
  // rather than wrongly showing "not set up for your area".
  const [fetchedTeams, setFetchedTeams] = useState([]);
  const [meTried, setMeTried] = useState(false);
  useEffect(() => {
    if (routeTeams.length) return;                 // session already has the team
    if (!shift?.driver) { setMeTried(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/portal-me', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
        });
        const d = await res.json();
        if (!cancelled && d && d.ok && Array.isArray(d.routes)) {
          setFetchedTeams(d.routes.filter(t => TEAMS.includes(t)));
        }
      } catch { /* leave empty — the oversight picker / "not set up" fallback covers it */ }
      finally { if (!cancelled) setMeTried(true); }
    })();
    return () => { cancelled = true; };
  }, [routeTeams.length, shift?.driver]);

  // Oversight users (owner/admins, team leaders) aren't tied to a single delivery
  // team, so their session carries no usable route team. Rather than block them
  // out, let them pick which team to book their own leave under.
  const [pickedTeam, setPickedTeam] = useState('');
  const myTeams = routeTeams.length ? routeTeams
    : fetchedTeams.length ? fetchedTeams
    : (pickedTeam ? [pickedTeam] : []);
  const primaryTeam = myTeams[0] || '';
  const teamField   = myTeams.length > 1 ? myTeams.join(',') : primaryTeam;
  // Show the team chooser only for an oversight user who genuinely has no team on
  // record (their profile fetch also came back empty).
  const needsTeamPick = oversight && routeTeams.length === 0 && fetchedTeams.length === 0 && meTried;

  const [entries, setEntries]   = useState([]);
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadErr, setLoadErr]   = useState('');

  const now = new Date();
  const [calYear, setCalYear]   = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const [start, setStart]       = useState('');
  const [end, setEnd]           = useState('');
  const [type, setType]         = useState('annual');
  const [reasonCat, setReasonCat] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [notes, setNotes]       = useState('');
  const [tcAccepted, setTcAccepted] = useState(null);
  const [dayMsg, setDayMsg]     = useState('');     // transient "not available" notice
  const [error, setError]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(null); // { start, end } once done

  // Amending / cancelling an already-approved booking (goes back to the manager to approve).
  const [amendingId, setAmendingId] = useState(null); // booking whose dates we're re-picking
  const [cancelId, setCancelId]     = useState(null); // booking in inline "confirm cancel" mode
  const [changeNote, setChangeNote] = useState('');   // optional reason for the cancel/amend
  const [changeBusy, setChangeBusy] = useState('');   // booking id currently saving a change

  // ── Load current availability ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadErr('');
      try {
        const res = await fetch(API);
        const d = await res.json();
        if (cancelled) return;
        setEntries(Array.isArray(d.entries) ? d.entries : []);
        setEvents(Array.isArray(d.events) ? d.events : []);
      } catch {
        if (!cancelled) setLoadErr('Could not load the team calendar. Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // A day is blocked if anyone in the driver's team already has APPROVED leave on it.
  // When amending, the driver's own booking doesn't block itself (they can keep/overlap its days).
  const blockedOn = (ds) => entries.some(e =>
    e.status === 'approved' && e.id !== amendingId && e.startDate <= ds && e.endDate >= ds &&
    myTeams.some(t => teamMatch(e.team, t)));

  // First blocked day within the current selection (range may span an N/A day).
  const rangeBlockedDay = useMemo(() => {
    if (!start || !end) return '';
    const d = new Date(start + 'T12:00:00');
    const last = new Date(end + 'T12:00:00');
    while (d <= last) {
      const ds = d.toISOString().slice(0, 10);
      if (blockedOn(ds)) return ds;
      d.setDate(d.getDate() + 1);
    }
    return '';
  }, [start, end, entries, myTeams, amendingId]);

  // Leave balances for this driver (from their approved entries).
  const balances = useMemo(() => {
    const name = shift?.name || '';
    // Holiday: count the rolling year both ways so already-booked upcoming leave still
    // counts against the allowance; sick counts are backward-looking ("days I've had").
    const holidayTaken = approvedWorkingDays(entries, name, HOLIDAY_TYPES, addMonths(today, -12), addMonths(today, 12));
    return {
      allowance: ANNUAL_ALLOWANCE,
      holidayTaken,
      holidayRemaining: Math.max(0, ANNUAL_ALLOWANCE - holidayTaken),
      sick12: approvedWorkingDays(entries, name, SICK_TYPES, addMonths(today, -12), today),
      sick6:  approvedWorkingDays(entries, name, SICK_TYPES, addMonths(today, -6),  today),
      sick3:  approvedWorkingDays(entries, name, SICK_TYPES, addMonths(today, -3),  today),
      sick1:  approvedWorkingDays(entries, name, SICK_TYPES, addMonths(today, -1),  today),
    };
  }, [entries, shift, today]);

  // This driver's own bookings — upcoming/current first, then recent past (last 6 months).
  const myBookings = useMemo(() => {
    const nm = (shift?.name || '').trim().toLowerCase();
    const cutoff = addMonths(today, -6);
    const mine = entries.filter(e => (e.name || '').trim().toLowerCase() === nm && e.endDate >= cutoff);
    const upcoming = mine.filter(e => e.endDate >= today).sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
    const past     = mine.filter(e => e.endDate <  today).sort((a, b) => (a.startDate > b.startDate ? -1 : 1));
    return [...upcoming, ...past];
  }, [entries, shift, today]);

  function pickDate(ds) {
    setError('');
    if (ds < today) return;                       // no leave in the past
    if (blockedOn(ds)) { setDayMsg(BLOCKED_MSG); return; }
    setDayMsg('');
    if (!start || (start && end) || ds < start) { setStart(ds); setEnd(''); return; }
    setEnd(ds);
  }

  function resetSelection() { setStart(''); setEnd(''); setError(''); setDayMsg(''); }

  function prevMonth() { setDayMsg(''); if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }
  function nextMonth() { setDayMsg(''); if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }

  async function submit() {
    setError('');
    if (!start || !end) { setError('Please choose your start and end dates.'); return; }
    if (end < start)    { setError('End date must be on or after the start date.'); return; }
    if (rangeBlockedDay) {
      setError(`${fmtShort(rangeBlockedDay)} is already taken in your team. ${BLOCKED_MSG}`);
      return;
    }
    if (type === 'annual' && rangeHasBlackout(start, end)) {
      setError('Annual leave isn’t available between 1 November and 24 December. Please choose other dates or speak to your team leader.');
      return;
    }
    // Per-request cap + rolling-12-month allowance, from the shared rules the
    // server enforces on write — so the preview and the verdict always agree.
    const allowance = checkAnnualAllowance(entries, {
      name: shift.name, type, startDate: start, endDate: end, excludeId: amendingId,
    });
    if (!allowance.ok) { setError(allowance.message); return; }
    if (!reasonCat) { setError('Please choose a reason for your time away.'); return; }
    if (reasonCat === 'Other' && !otherReason.trim()) { setError('Please describe your reason.'); return; }
    if (tcAccepted !== true) { setError('Please tick the box confirming you’ll return to work on your due date before submitting.'); return; }

    const reasonLabel = reasonCat === 'Other' ? `Other — ${otherReason.trim()}` : reasonCat;
    const finalNotes  = [`Leave Reason: ${reasonLabel}`, notes.trim()].filter(Boolean).join('\n\n');

    setSubmitting(true);

    // Re-fetch before saving so we append to the FULL dataset (the roster-data
    // POST replaces everything). Guard against clobbering real data with a
    // partial/empty Blobs cache — mirrors the QR flow's safety check.
    let serverConfirmed = false;
    let baseEntries = entries, baseEvents = events;
    try {
      const res = await fetch(API);
      if (res.ok) {
        const d = await res.json();
        const de = Array.isArray(d.entries) ? d.entries : [];
        const dv = Array.isArray(d.events) ? d.events : [];
        if (de.length || dv.length) {
          serverConfirmed = true;
          if (de.length >= entries.length) { baseEntries = de; baseEvents = dv; }
        }
      }
    } catch { /* keep in-memory copy */ }

    if (!serverConfirmed && baseEntries.length === 0) {
      setError('Could not verify current bookings — the server may be busy. Please wait a moment and try again. Your request was NOT submitted.');
      setSubmitting(false);
      return;
    }

    // Re-check the conflict against the freshest data (someone may have just been approved).
    const stillBlocked = (() => {
      const d = new Date(start + 'T12:00:00');
      const last = new Date(end + 'T12:00:00');
      while (d <= last) {
        const ds = d.toISOString().slice(0, 10);
        if (baseEntries.some(e => e.status === 'approved' && e.id !== amendingId && e.startDate <= ds && e.endDate >= ds && myTeams.some(t => teamMatch(e.team, t)))) return ds;
        d.setDate(d.getDate() + 1);
      }
      return '';
    })();
    if (stillBlocked) {
      setEntries(baseEntries); setEvents(baseEvents);
      setError(`${fmtShort(stillBlocked)} has just been taken in your team. ${BLOCKED_MSG}`);
      setSubmitting(false);
      return;
    }

    // Amend mode: attach a pending "change" to the existing approved booking instead of
    // creating a new entry. The booking stays approved (still holds its old dates) until
    // the manager approves the new dates in their queue.
    // Baseline we loaded, sent so the server 3-way-merges our request onto the
    // latest data instead of overwriting entries added since (see roster-merge.js).
    const base = { entries: baseEntries, events: baseEvents };
    let payload, confirmMsg;
    if (amendingId) {
      const changeObj = { kind: 'amend', newStart: start, newEnd: end, reason: finalNotes, requestedAt: new Date().toISOString() };
      payload = { entries: baseEntries.map(e => e.id === amendingId ? { ...e, change: changeObj } : e), events: baseEvents, base };
      confirmMsg = `Hi ${shift.name}, your request to change your leave to ${fmtShort(start)}–${fmtShort(end)} has been sent to your manager for approval. Your original dates stand until it’s confirmed.`;
    } else {
      const newEntry = {
        id: uid(), name: shift.name, team: teamField, type,
        startDate: start, endDate: end, notes: finalNotes,
        status: 'pending', createdAt: new Date().toISOString(),
        phone: shift.phone || '',
      };
      payload = { entries: [...baseEntries, newEntry], events: baseEvents, base };
      confirmMsg = `Hi ${shift.name}, your time-away request for ${fmtShort(start)}–${fmtShort(end)} has been received and is being reviewed. We’ll confirm the outcome shortly.`;
    }

    try {
      const r = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const rd = await r.json().catch(() => ({}));
      if (!r.ok || rd.ok === false) throw new Error('save failed');
      // Adopt the merged result the server echoes back (falls back to our optimistic copy).
      setEntries(Array.isArray(rd.entries) ? rd.entries : payload.entries);
      setEvents(Array.isArray(rd.events) ? rd.events : payload.events);
      // Fire-and-forget confirmation SMS (only if we have a number)
      if (shift.phone) {
        fetch(SMS, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: shift.phone, message: confirmMsg }),
        }).catch(() => {});
      }
      setSubmitted({ start, end, amend: !!amendingId });
      setAmendingId(null);
    } catch {
      setError('Could not submit your request — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function startAnother() {
    setSubmitted(null);
    resetSelection();
    setType('annual'); setReasonCat(''); setOtherReason(''); setNotes(''); setTcAccepted(null);
    setAmendingId(null);
  }

  // ── Amend / cancel an already-approved booking ────────────────────────────
  // Enter "amend" mode for a booking: pre-fill its dates/type into the picker and
  // scroll the driver down to choose new dates. Submitting routes through submit().
  function startAmend(b) {
    setCancelId(null); setChangeNote(''); setError(''); setDayMsg('');
    setAmendingId(b.id);
    setStart(b.startDate); setEnd(b.endDate);
    setType(b.type === 'sick' ? 'sick' : 'annual');
    setReasonCat(''); setOtherReason(''); setNotes(''); setTcAccepted(null);
    setCalYear(new Date(b.startDate + 'T12:00:00').getFullYear());
    setCalMonth(new Date(b.startDate + 'T12:00:00').getMonth());
    if (typeof window !== 'undefined') setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 50);
  }
  function stopAmend() {
    setAmendingId(null);
    resetSelection();
    setReasonCat(''); setOtherReason(''); setNotes(''); setTcAccepted(null);
  }

  // Shared write: set (or clear, when change===null) the pending change on one booking,
  // re-fetching first so we append to the full dataset (POST replaces everything).
  async function saveEntryChange(entryId, change) {
    setChangeBusy(entryId); setError('');
    let baseEntries = entries, baseEvents = events, serverConfirmed = false;
    try {
      const res = await fetch(API);
      if (res.ok) {
        const d = await res.json();
        const de = Array.isArray(d.entries) ? d.entries : [];
        const dv = Array.isArray(d.events) ? d.events : [];
        if (de.length || dv.length) { serverConfirmed = true; if (de.length >= entries.length) { baseEntries = de; baseEvents = dv; } }
      }
    } catch { /* keep in-memory copy */ }
    if (!serverConfirmed && baseEntries.length === 0) {
      setError('Could not reach the server — please wait a moment and try again.');
      setChangeBusy(''); return false;
    }
    const nextEntries = baseEntries.map(e => {
      if (e.id !== entryId) return e;
      if (change === null) { const { change: _drop, ...rest } = e; return rest; }
      return { ...e, change };
    });
    try {
      const r = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: nextEntries, events: baseEvents }),
      });
      const rd = await r.json().catch(() => ({}));
      if (!r.ok || rd.ok === false) throw new Error('save failed');
      setEntries(nextEntries); setEvents(baseEvents);
      setChangeBusy(''); return true;
    } catch {
      setError('Could not save your request — please try again.');
      setChangeBusy(''); return false;
    }
  }

  async function confirmCancel(b) {
    const ok = await saveEntryChange(b.id, { kind: 'cancel', reason: changeNote.trim(), requestedAt: new Date().toISOString() });
    if (!ok) return;
    setCancelId(null); setChangeNote('');
    if (shift.phone) {
      fetch(SMS, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: shift.phone, message: `Hi ${shift.name}, your request to cancel your leave ${fmtShort(b.startDate)}–${fmtShort(b.endDate)} has been sent to your manager for approval.` }),
      }).catch(() => {});
    }
  }

  async function withdrawChange(b) {
    await saveEntryChange(b.id, null);
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    card:  { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 18 },
    label: { display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hp-dim)', marginBottom: 6, fontWeight: 700 },
    select:{ width: '100%', padding: '11px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.95rem', fontFamily: 'var(--font-body)' },
    locked:{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.95rem' },
  };

  // Oversight user (owner/admin, team leader) with no route team — let them pick
  // which team they're booking their own leave under, rather than blocking them.
  if (!loading && myTeams.length === 0 && needsTeamPick) {
    return (
      <div style={{ ...S.card }}>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <Calendar size={26} style={{ color: 'var(--hp-gold)', marginBottom: 8 }} />
          <div style={{ fontWeight: 700, color: 'var(--hp-text)', marginBottom: 6 }}>Which team are you booking under?</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>Your account oversees all teams — choose the team this time-away request belongs to.</div>
        </div>
        <label style={S.label}>Team</label>
        <select style={S.select} value={pickedTeam} onChange={e => setPickedTeam(e.target.value)}>
          <option value="">Select a team…</option>
          {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    );
  }

  // No team set up — can't compute availability. Send them to their TL. Wait for the
  // profile fetch (meTried) first so a session that just lost its team self-heals
  // instead of flashing this message.
  if (!loading && meTried && myTeams.length === 0) {
    return (
      <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}>
        <Calendar size={26} style={{ color: 'var(--hp-gold)', marginBottom: 8 }} />
        <div style={{ fontWeight: 700, color: 'var(--hp-text)', marginBottom: 6 }}>Time-away requests aren’t set up for your area yet</div>
        <div style={{ fontSize: '0.85rem' }}>Please speak to your team leader to book time away.</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ ...S.card, textAlign: 'center', padding: 28 }}>
        <CheckCircle size={40} style={{ color: 'var(--hp-green)', marginBottom: 12 }} />
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>{submitted.amend ? 'CHANGE REQUESTED' : 'REQUEST SUBMITTED'}</div>
        <div style={{ fontSize: '0.9rem', color: 'var(--hp-dim)', marginTop: 8 }}>
          {fmtFull(submitted.start)}{submitted.end !== submitted.start ? <> → {fmtFull(submitted.end)}</> : null}
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 10, lineHeight: 1.5 }}>
          Your {submitted.amend ? 'date change is' : 'request is'} now <strong style={{ color: 'var(--hp-amber)' }}>awaiting approval</strong>.
          {submitted.amend ? ' Your original dates stay booked until your manager confirms the change.' : ' You’ll be notified once your team leader confirms it.'}
        </div>
        <button onClick={startAnother}
          style={{ marginTop: 20, padding: '11px 22px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          {submitted.amend ? 'Back to my time away' : 'Submit another request'}
        </button>
      </div>
    );
  }

  // ── Calendar grid ─────────────────────────────────────────────────────────
  const monthLabel = new Date(calYear, calMonth, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }).toUpperCase();
  const firstDow = (new Date(calYear, calMonth, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const daysInPrev = new Date(calYear, calMonth, 0).getDate();
  const cells = [];
  for (let i = firstDow - 1; i >= 0; i--) cells.push({ date: toISO(calYear, calMonth - 1, daysInPrev - i), other: true });
  for (let d = 1; d <= daysInMonth; d++)  cells.push({ date: toISO(calYear, calMonth, d), other: false });
  while (cells.length < 42)                cells.push({ date: toISO(calYear, calMonth + 1, cells.length - firstDow - daysInMonth + 1), other: true });

  const teamColor = TEAM_COLOR[primaryTeam] || 'var(--hp-gold)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      {/* Header */}
      <div style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={18} style={{ color: 'var(--hp-gold)' }} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>REQUEST TIME AWAY</span>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 4 }}>
            Pick the days you’d like off. Greyed-out days are already taken in your team.
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Team</div>
          <div style={{ fontWeight: 700, color: teamColor, fontSize: '0.9rem' }}>{myTeams.join(' · ')}</div>
        </div>
      </div>

      {/* Leave balance */}
      {!loading && !loadErr && (
        <div style={S.card}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hp-dim)', fontWeight: 700, marginBottom: 10 }}>Holiday leave</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { label: 'Allowance', value: balances.allowance, sub: 'days / year', color: 'var(--hp-text)' },
              { label: 'Taken', value: balances.holidayTaken, sub: 'last 12 mo', color: 'var(--hp-text)' },
              { label: 'Remaining', value: balances.holidayRemaining, sub: 'to book', color: balances.holidayRemaining > 0 ? 'var(--hp-green)' : 'var(--hp-red)' },
            ].map(s => (
              <div key={s.label} style={statBox}>
                <div style={statValue(s.color)}>{s.value}</div>
                <div style={statLabel}>{s.label}</div>
                <div style={statSub}>{s.sub}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hp-dim)', fontWeight: 700, margin: '16px 0 10px' }}>Sick days taken</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {[
              { label: '1 mo', value: balances.sick1 },
              { label: '3 mo', value: balances.sick3 },
              { label: '6 mo', value: balances.sick6 },
              { label: '12 mo', value: balances.sick12 },
            ].map(s => (
              <div key={s.label} style={statBox}>
                <div style={statValue(s.value === 0 ? 'var(--hp-dim)' : 'var(--hp-amber)')}>{s.value}</div>
                <div style={statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', marginTop: 10 }}>Counts approved leave only · holiday allowance is {ANNUAL_ALLOWANCE} working days per rolling 12 months.</div>
        </div>
      )}

      {/* My time away — the driver's own bookings + their status */}
      {!loading && !loadErr && (
        <div style={S.card}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hp-dim)', fontWeight: 700, marginBottom: 12 }}>My time away</div>
          {myBookings.length === 0 ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>You have no time away booked yet. Request some below.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {myBookings.map(b => {
                const meta = bookingStatusMeta(b.status);
                const isPast = b.endDate < today;
                const wd = workingDayCount(b.startDate, b.endDate);
                const hasChange  = !!(b.change && b.change.kind);
                const canModify  = b.status === 'approved' && b.endDate >= today && !hasChange;
                const isAmending = amendingId === b.id;
                const isCancelling = cancelId === b.id;
                const busy = changeBusy === b.id;
                return (
                  <div key={b.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius)', border: `1px solid ${isAmending ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: 'var(--hp-bg)', opacity: isPast ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: 'var(--hp-text)', fontSize: '0.86rem' }}>{rangeLabel(b.startDate, b.endDate)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>{bookingTypeLabel(b.type)} · {wd} working day{wd !== 1 ? 's' : ''}{isPast ? ' · past' : ''}</div>
                      </div>
                      <span style={{ flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 999, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                        {b.status === 'approved' ? '✓ ' : ''}{meta.label}
                      </span>
                    </div>

                    {/* A cancel/amend request is in with the manager */}
                    {hasChange && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: '0.74rem', color: 'var(--hp-amber)', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', padding: '7px 10px' }}>
                        <span style={{ minWidth: 0 }}>
                          ⏳ {b.change.kind === 'cancel'
                            ? 'Cancellation requested'
                            : <>Change to <strong>{fmtShort(b.change.newStart)}–{fmtShort(b.change.newEnd)}</strong> requested</>}
                          {' '}— awaiting approval
                        </span>
                        <button onClick={() => withdrawChange(b)} disabled={busy}
                          style={{ flexShrink: 0, background: 'none', border: 'none', color: 'var(--hp-dim)', textDecoration: 'underline', cursor: busy ? 'default' : 'pointer', fontSize: '0.72rem', fontWeight: 700, fontFamily: 'var(--font-body)', opacity: busy ? 0.5 : 1 }}>
                          {busy ? '…' : 'Undo'}
                        </button>
                      </div>
                    )}

                    {/* Amend / Cancel actions on an upcoming approved booking */}
                    {canModify && !isCancelling && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => startAmend(b)} disabled={isAmending}
                          style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: isAmending ? 'var(--hp-gold)' : 'transparent', color: isAmending ? '#1a1a1a' : 'var(--hp-gold)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                          {isAmending ? 'Amending…' : 'Amend dates'}
                        </button>
                        {isAmending ? (
                          <button onClick={stopAmend}
                            style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                            Stop amending
                          </button>
                        ) : (
                          <button onClick={() => { setCancelId(b.id); setChangeNote(''); setError(''); }}
                            style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                            Cancel booking
                          </button>
                        )}
                      </div>
                    )}

                    {/* Amend hint — the picker below is now in amend mode */}
                    {isAmending && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                        Pick your new dates on the calendar below, then submit. Your original dates stay booked until your manager approves the change.
                      </div>
                    )}

                    {/* Inline confirm-cancel panel */}
                    {isCancelling && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--hp-border)', paddingTop: 8 }}>
                        <div style={{ fontSize: '0.76rem', color: 'var(--hp-text)' }}>Ask your manager to cancel this booking? It stays in place until they approve.</div>
                        <textarea value={changeNote} onChange={e => setChangeNote(e.target.value)} placeholder="Reason (optional) — anything your manager should know…"
                          style={{ width: '100%', minHeight: 52, resize: 'vertical', padding: '9px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontSize: '0.85rem', fontFamily: 'var(--font-body)' }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => confirmCancel(b)} disabled={busy}
                            style={{ flex: 1, padding: '9px 10px', borderRadius: 'var(--radius)', border: 'none', background: 'var(--hp-red)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: busy ? 'default' : 'pointer', fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1 }}>
                            {busy ? 'Sending…' : 'Send cancellation'}
                          </button>
                          <button onClick={() => { setCancelId(null); setChangeNote(''); }} disabled={busy}
                            style={{ flex: 1, padding: '9px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                            Keep booking
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--hp-dim)' }}>Loading team calendar…</div>
      ) : loadErr ? (
        <div style={{ ...S.card, color: 'var(--hp-red)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={16} /> {loadErr}
        </div>
      ) : (
        <>
          {/* Calendar */}
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <button onClick={prevMonth} style={navBtn}>‹</button>
              <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, color: 'var(--hp-text)', letterSpacing: '0.04em' }}>{monthLabel}</div>
              <button onClick={nextMonth} style={navBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: '0.6rem', color: 'var(--hp-dim)', fontWeight: 700, padding: '2px 0' }}>{d}</div>)}
              {cells.map(({ date, other }, i) => {
                const dnum = new Date(date + 'T12:00:00').getDate();
                if (other) return <div key={i} style={{ ...calCell, color: 'var(--hp-dim)', opacity: 0.25 }}>{dnum}</div>;
                const past    = date < today;
                const blocked = blockedOn(date);
                const bo      = type === 'annual' && isBlackout(date);
                const isStart = date === start, isEnd = date === end;
                const inRange = start && end && date > start && date < end;
                const sel     = isStart || isEnd;

                let style = { ...calCell };
                if (past)         style = { ...style, color: 'var(--hp-dim)', opacity: 0.3, cursor: 'default' };
                else if (blocked) style = { ...style, background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.4)', color: 'var(--hp-red)', cursor: 'pointer' };
                else if (sel)     style = { ...style, background: 'var(--hp-gold)', color: '#1a1a1a', fontWeight: 800, cursor: 'pointer' };
                else if (inRange) style = { ...style, background: 'rgba(212,175,55,0.18)', color: 'var(--hp-text)', cursor: 'pointer' };
                else if (bo)      style = { ...style, background: 'rgba(239,68,68,0.05)', color: 'var(--hp-text)', cursor: 'pointer' };
                else              style = { ...style, color: 'var(--hp-text)', cursor: 'pointer' };

                return (
                  <div key={i} style={style} onClick={() => !past && pickDate(date)}>
                    <span>{dnum}</span>
                    {blocked && <span style={{ fontSize: '0.5rem', fontWeight: 800, lineHeight: 1 }}>N/A</span>}
                    {!blocked && bo && <span style={{ fontSize: '0.5rem', lineHeight: 1 }}>🚫</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: '0.6rem', color: 'var(--hp-dim)', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 3 }} /> N/A — taken in your team</span>
              {type === 'annual' && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>🚫 Blackout (no annual leave)</span>}
            </div>
            {dayMsg && (
              <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius)', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--hp-red)', fontSize: '0.82rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                <AlertCircle size={15} style={{ flexShrink: 0 }} /> {BLOCKED_MSG}
              </div>
            )}
          </div>

          {/* Selection summary */}
          <div style={{ ...S.card, display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={S.label}>Start</div>
              <div style={{ color: start ? 'var(--hp-text)' : 'var(--hp-dim)', fontWeight: 600 }}>{start ? fmtFull(start) : 'Tap a day above'}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={S.label}>End</div>
              <div style={{ color: end ? 'var(--hp-text)' : 'var(--hp-dim)', fontWeight: 600 }}>{end ? fmtFull(end) : start ? 'Tap the last day' : '—'}</div>
            </div>
            {start && (
              <button onClick={resetSelection} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: 'var(--hp-gold)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>Reset</button>
            )}
          </div>

          {/* Details — only once dates are chosen */}
          {start && end && !rangeBlockedDay && (
            <div style={{ ...S.card, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {amendingId && (
                <div style={{ fontSize: '0.78rem', color: 'var(--hp-gold)', fontWeight: 700, background: 'rgba(212,175,55,0.10)', border: '1px solid rgba(212,175,55,0.35)', borderRadius: 'var(--radius)', padding: '9px 11px' }}>
                  ✎ Amending your existing booking — these new dates go to your manager to approve.
                </div>
              )}
              <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                <strong style={{ color: 'var(--hp-text)' }}>{workingDayCount(start, end)}</strong> working day{workingDayCount(start, end) !== 1 ? 's' : ''} {amendingId ? 'in the new dates' : 'requested'}
              </div>

              <div>
                <label style={S.label}>Name</label>
                <div style={S.locked}><Lock size={13} style={{ color: 'var(--hp-dim)' }} /> {shift.name}</div>
              </div>

              <div>
                <label style={S.label}>Leave Type</label>
                <select style={S.select} value={type} onChange={e => { setType(e.target.value); setError(''); }}>
                  <option value="annual">Annual Leave</option>
                  <option value="sick">Sick Leave</option>
                </select>
              </div>

              <div>
                <label style={S.label}>Reason <span style={{ color: 'var(--hp-red)' }}>*</span></label>
                <select style={S.select} value={reasonCat} onChange={e => { setReasonCat(e.target.value); setError(''); }}>
                  <option value="">— Select a reason —</option>
                  <option value="Holiday / Recreational">Holiday / Recreational</option>
                  <option value="Personal Leave">Personal Leave</option>
                  <option value="Family Leave">Family Leave</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {reasonCat === 'Other' && (
                <div>
                  <label style={S.label}>Please describe <span style={{ color: 'var(--hp-red)' }}>*</span></label>
                  <textarea style={{ ...S.select, minHeight: 70, resize: 'vertical' }} value={otherReason} onChange={e => setOtherReason(e.target.value)} placeholder="Describe the reason for your leave…" />
                </div>
              )}

              <div>
                <label style={S.label}>Notes (optional)</label>
                <textarea style={{ ...S.select, minHeight: 60, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything your team leader should know…" />
              </div>

              {/* Return-to-work acknowledgement — must be ticked to submit */}
              <div style={{ border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius)', padding: 14, background: 'rgba(239,68,68,0.05)' }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--hp-text)', lineHeight: 1.6, marginBottom: 12 }}>
                  ⚠ <strong>Important:</strong> Failure to return from leave on time, or as per your agreed schedule, may result in <strong>termination of your contractor agreement</strong>.
                </div>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: '0.83rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>
                  <input type="checkbox" checked={tcAccepted === true}
                    onChange={e => { setTcAccepted(e.target.checked); setError(''); }}
                    style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, accentColor: 'var(--hp-gold)', cursor: 'pointer' }} />
                  <span>I confirm I will <strong>return to work on my agreed return date</strong> (after my leave ends on {fmtFull(end)}), and I accept the terms above.</span>
                </label>
              </div>

              {error && (
                <div style={{ padding: '11px 12px', borderRadius: 'var(--radius)', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--hp-red)', fontSize: '0.83rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
                </div>
              )}

              <button onClick={submit} disabled={submitting || tcAccepted !== true}
                style={{ width: '100%', padding: 14, borderRadius: 'var(--radius)', border: 'none', cursor: submitting || tcAccepted !== true ? 'default' : 'pointer', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.04em', fontFamily: 'var(--font-body)', background: 'var(--hp-gold)', color: '#1a1a1a', opacity: submitting || tcAccepted !== true ? 0.5 : 1 }}>
                {submitting ? 'Submitting…' : amendingId ? 'Submit change request' : 'Submit Request'}
              </button>
            </div>
          )}

          {/* Range crosses an N/A day */}
          {start && end && rangeBlockedDay && (
            <div style={{ ...S.card, color: 'var(--hp-red)', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.85rem' }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span><strong>{fmtShort(rangeBlockedDay)}</strong> in your selection is already taken in your team. {BLOCKED_MSG}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const navBtn = { width: 34, height: 34, borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const calCell = { aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 6, fontSize: '0.85rem', userSelect: 'none' };
const statBox = { background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 6px', textAlign: 'center' };
const statValue = (color) => ({ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1.1 });
const statLabel = { fontSize: '0.66rem', color: 'var(--hp-text)', fontWeight: 700, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.04em' };
const statSub = { fontSize: '0.6rem', color: 'var(--hp-dim)', marginTop: 1 };

const BOOKING_STATUS = {
  approved: { label: 'Confirmed',         color: 'var(--hp-green)', bg: 'rgba(34,197,94,0.13)',  border: 'rgba(34,197,94,0.35)' },
  pending:  { label: 'Awaiting approval', color: 'var(--hp-amber)', bg: 'rgba(245,158,11,0.13)', border: 'rgba(245,158,11,0.35)' },
  rejected: { label: 'Declined',          color: 'var(--hp-red)',   bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)' },
  withdrawn:{ label: 'Withdrawn',         color: 'var(--hp-dim)',   bg: 'rgba(148,163,184,0.13)', border: 'rgba(148,163,184,0.35)' },
};
function bookingStatusMeta(status) { return BOOKING_STATUS[status] || BOOKING_STATUS.pending; }
function bookingTypeLabel(t) { return t === 'sick' ? 'Sick leave' : t === 'exception' ? 'Exception' : 'Annual leave'; }
function rangeLabel(s, e) { return s === e ? fmtFull(s) : `${fmtShort(s)} → ${fmtFull(e)}`; }
